<template>
  <div class="admin-container">
    <div class="admin-header">
      <div>
        <h1>管理后台</h1>
        <p class="admin-header__subtitle">先发需求，再发布任务，最后统一跟进领取人与项目进度。</p>
      </div>
      <div class="user-info">
        <el-tag :type="userStore.isSuperAdmin ? 'danger' : 'warning'" size="small">
          {{ userStore.isSuperAdmin ? '超级管理员' : '管理员' }}
        </el-tag>
        <span class="username">{{ userStore.userInfo?.username }}</span>
        <el-button size="small" @click="handleLogout">退出</el-button>
      </div>
    </div>

    <section class="admin-overview">
      <div class="admin-overview__stats">
        <article class="admin-stat-card">
          <span>项目总数</span>
          <strong>{{ projectRows.length }}</strong>
          <small>当前管理员可见的全部项目</small>
        </article>
        <article class="admin-stat-card admin-stat-card--accent">
          <span>待处理项目</span>
          <strong>{{ pendingProjectCount }}</strong>
          <small>优先处理方案、剧本、分镜与成品审批</small>
        </article>
        <article class="admin-stat-card admin-stat-card--soft">
          <span>任务广场</span>
          <strong>{{ taskSquareItems.length }}</strong>
          <small>已发布协作任务，支持多人领取</small>
        </article>
      </div>
    </section>

    <el-tabs v-model="activeTab" class="admin-tabs">
      <!-- User Management Tab - Only for Super Admin -->
      <el-tab-pane v-if="userStore.isSuperAdmin" label="用户管理" name="users">
        <div class="tab-content">
          <div class="toolbar">
            <el-button type="primary" @click="showAddUserDialog">
              <el-icon><Plus /></el-icon>
              添加用户
            </el-button>
            <div class="toolbar-actions">
              <el-input
                v-model="searchQuery"
                placeholder="搜索用户..."
                style="width: 300px"
                clearable
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
              <el-button :loading="usersLoading" @click="loadUsers">刷新用户</el-button>
            </div>
          </div>

          <el-table :data="filteredUsers" style="width: 100%" border v-loading="usersLoading">
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="username" label="用户名" />
            <el-table-column prop="email" label="邮箱" />
            <el-table-column prop="role" label="角色" width="120">
              <template #default="{ row }">
                <el-tag :type="getRoleTagType(row.role)" size="small">
                  {{ getRoleLabel(row.role) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="is_active" label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="row.is_active ? 'success' : 'info'" size="small">
                  {{ row.is_active ? '启用' : '停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="注册时间" width="180">
              <template #default="{ row }">
                {{ formatDate(row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="200" fixed="right">
              <template #default="{ row }">
                <el-button
                  v-if="userStore.isSuperAdmin"
                  size="small"
                  type="primary"
                  @click="editUser(row)"
                >
                  编辑
                </el-button>
                <el-button
                  v-if="userStore.isSuperAdmin && row.id !== userStore.userInfo?.id"
                  size="small"
                  type="danger"
                  @click="deleteUser(row)"
                >
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <!-- Projects Tab -->
      <el-tab-pane label="项目管理" name="projects">
        <div class="tab-content">
          <div class="toolbar toolbar--projects toolbar-surface">
            <div class="toolbar-copy">
              <strong>项目处理与任务发布</strong>
              <span>先创建需求，再从项目行里处理审批或发布任务；重复按钮已经收敛到主操作和更多菜单里。</span>
            </div>
            <div class="toolbar-cluster">
              <div class="toolbar-actions">
                <el-button type="primary" @click="router.push('/submit')">创建需求</el-button>
                <el-button plain @click="activeTab = 'task-square'">发布任务 / 查看领取</el-button>
              </div>
              <div class="toolbar-actions">
                <el-input
                  v-model="projectSearchQuery"
                  placeholder="搜索项目标题..."
                  style="width: 320px"
                  clearable
                >
                  <template #prefix>
                    <el-icon><Search /></el-icon>
                  </template>
                </el-input>
                <el-button :loading="projectsLoading" @click="loadProjects">刷新项目</el-button>
              </div>
            </div>
          </div>

          <el-table :data="filteredProjects" style="width: 100%" border v-loading="projectsLoading">
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="title" label="项目名称" min-width="220" />
            <el-table-column prop="genre" label="类型" width="140">
              <template #default="{ row }">
                {{ row.genre || '未设置' }}
              </template>
            </el-table-column>
            <el-table-column label="归属用户" width="140">
              <template #default="{ row }">
                {{ row.ownerName }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="140">
              <template #default="{ row }">
                <el-tag :type="projectDisplayStatusType(row)" size="small">
                  {{ projectDisplayStatusLabel(row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="下一步" min-width="180">
              <template #default="{ row }">
                {{ projectNextActionLabel(row) }}
              </template>
            </el-table-column>
            <el-table-column prop="target_duration" label="目标时长" width="120">
              <template #default="{ row }">
                {{ row.target_duration ? `${row.target_duration} 分钟` : '未设置' }}
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="创建时间" width="180">
              <template #default="{ row }">
                {{ formatDate(row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="260" fixed="right">
              <template #default="{ row }">
                <el-space wrap class="project-action-cell">
                  <el-button size="small" type="primary" @click="handleProjectPrimaryAction(row)">
                    {{ projectPrimaryActionLabel(row) }}
                  </el-button>
                  <el-button
                    size="small"
                    plain
                    :loading="publishingProjectId === Number(row.id)"
                    @click="handleProjectTaskAction(row)"
                  >
                    {{ projectTaskActionLabel(row) }}
                  </el-button>
                  <el-dropdown trigger="click" @command="(command) => handleProjectMenuCommand(row, command)">
                    <el-button size="small">
                      更多
                      <el-icon class="el-icon--right"><ArrowDown /></el-icon>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item command="workspace">进入工作台</el-dropdown-item>
                        <el-dropdown-item command="task-square">打开任务广场</el-dropdown-item>
                        <el-dropdown-item command="delete" divided>删除项目</el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </el-space>
              </template>
            </el-table-column>
          </el-table>

          <el-empty v-if="!projectsLoading && !filteredProjects.length" description="暂无项目数据" />
        </div>
      </el-tab-pane>

      <el-tab-pane label="任务广场" name="task-square">
        <div class="tab-content">
          <div class="toolbar toolbar--projects toolbar-surface">
            <div class="toolbar-copy">
              <strong>任务发布与领取进度</strong>
              <span>管理员在这里看所有已发布任务，快速回到需求创建页，或把现有项目发布成任务。</span>
            </div>
            <div class="toolbar-actions">
              <el-button type="primary" @click="router.push('/submit')">创建需求</el-button>
              <el-button plain @click="router.push('/task-square')">打开任务广场页面</el-button>
              <el-button :loading="taskSquareLoading" @click="loadTaskSquare">刷新任务</el-button>
            </div>
          </div>

          <el-table :data="taskSquareItems" style="width: 100%" border v-loading="taskSquareLoading">
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="title" label="任务标题" min-width="220" />
            <el-table-column prop="source_project.title" label="来源项目" min-width="200">
              <template #default="{ row }">
                {{ row.source_project?.title || `项目 ${row.source_project_id}` }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="120">
              <template #default="{ row }">
                <el-tag :type="taskSquareStatusType(row.status)" size="small">{{ taskSquareStatusLabel(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="领取人数" width="100">
              <template #default="{ row }">
                {{ row.claim_count }}
              </template>
            </el-table-column>
            <el-table-column label="进展" min-width="320">
              <template #default="{ row }">
                <div v-if="row.assignments.length" class="assignment-pills">
                  <span v-for="assignment in row.assignments" :key="assignment.id" class="assignment-pill">
                    {{ assignment.assignee?.username || `用户 ${assignment.assignee_id}` }} · {{ assignment.progress_percent }}%
                  </span>
                </div>
                <span v-else class="muted-text">还没有人领取</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="260" fixed="right">
              <template #default="{ row }">
                <el-button size="small" type="primary" @click="router.push(buildGenerationRoute(row.source_project_id))">查看来源项目</el-button>
                <el-button v-if="row.status !== 'open'" size="small" plain @click="handleUpdateTaskSquareStatus(row.id, 'open')">开放</el-button>
                <el-button v-else size="small" plain @click="handleUpdateTaskSquareStatus(row.id, 'paused')">暂停</el-button>
                <el-button v-if="row.status !== 'closed'" size="small" type="danger" plain @click="handleUpdateTaskSquareStatus(row.id, 'closed')">关闭</el-button>
              </template>
            </el-table-column>
          </el-table>

          <el-empty v-if="!taskSquareLoading && !taskSquareItems.length" description="当前还没有已发布任务。你可以先创建需求，或在项目管理里把现有项目发布为任务。" />
        </div>
      </el-tab-pane>

      <!-- Settings Tab -->
      <el-tab-pane label="系统设置" name="settings">
        <div class="tab-content">
          <el-card shadow="never" class="settings-card">
            <template #header>
              <div class="settings-card__header">
                <div>
                  <h3>模型与密钥配置</h3>
                  <p>管理员在这里进入统一配置中心，维护文本接入、多模态生成、用户模型目录与 OSS 等运行时参数。</p>
                </div>
                <el-button type="primary" @click="router.push('/settings')">打开配置中心</el-button>
              </div>
            </template>

            <div class="settings-card__body">
              <el-tag type="success">运行时即时生效</el-tag>
              <span>保存后会同步覆盖当前后端运行中的 settings，页面也会显示哪些链路是“已实现可用”，哪些仍需要继续补接。</span>
            </div>
          </el-card>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Add/Edit User Dialog -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑用户' : '添加用户'"
      width="500px"
    >
      <el-form
        ref="userFormRef"
        :model="userForm"
        :rules="userFormRules"
        label-width="80px"
      >
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" :disabled="isEdit" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item v-if="!isEdit" label="密码" prop="password">
          <el-input
            v-model="userForm.password"
            type="password"
            placeholder="请输入密码"
            show-password
          />
        </el-form-item>
        <el-form-item v-if="isEdit" label="重置密码" prop="resetPassword">
          <el-input
            v-model="userForm.resetPassword"
            type="password"
            placeholder="留空则不修改密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="userForm.role" placeholder="请选择角色" style="width: 100%">
            <el-option label="普通用户" value="user" />
            <el-option label="只读用户" value="viewer" />
            <el-option label="管理员" value="admin" />
            <el-option label="超级管理员" value="super_admin" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="is_active">
          <el-switch
            v-model="userForm.is_active"
            inline-prompt
            active-text="启用"
            inactive-text="停用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox, type FormInstance, type FormRules } from 'element-plus'
import { ArrowDown, Plus, Search } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'
import type { UserRole, User } from '@/types/auth'
import { createUser, deleteUser as deleteUserRequest, getUsers, updateUser } from '@/api/auth'
import { projectApi, type Project } from '@/api'
import { taskSquareApi, type TaskSquareStatus, type TaskSquareTask } from '@/api/taskSquare'
import { getWorkflow } from '@/api/workflow'
import type { WorkflowInstance } from '@/types/workflow'
import { getLatestReviewableStage } from '@/utils/workflowPresentation'
import { buildGenerationRoute } from '@/utils/workflowGeneration'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const resolveAdminTab = (tab: unknown) => {
  const value = typeof tab === 'string' ? tab : ''
  if (value === 'users' || value === 'projects' || value === 'task-square' || value === 'settings') {
    return value
  }
  return null
}

const activeTab = ref(resolveAdminTab(route.query.tab) || (userStore.isSuperAdmin ? 'users' : 'projects'))
const searchQuery = ref('')
const projectSearchQuery = ref('')
const dialogVisible = ref(false)
const isEdit = ref(false)
const submitLoading = ref(false)
const usersLoading = ref(false)
const projectsLoading = ref(false)
const taskSquareLoading = ref(false)
const userFormRef = ref<FormInstance>()
const projects = ref<Project[]>([])
const users = ref<User[]>([])
const taskSquareItems = ref<TaskSquareTask[]>([])
const workflowMap = ref<Record<string, WorkflowInstance>>({})
const editingUserId = ref<number | null>(null)
const publishingProjectId = ref<number | null>(null)

type AdminProjectRow = Project & {
  workflowState: string
  pendingApprovalStage: string | null
  ownerName: string
}

const userForm = reactive({
  username: '',
  email: '',
  password: '',
  resetPassword: '',
  role: 'user' as UserRole,
  is_active: true,
})

const userFormRules: FormRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度在3-20个字符', trigger: 'blur' },
  ],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '邮箱格式不正确', trigger: 'blur' },
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码长度至少6 位', trigger: 'blur' },
  ],
  role: [
    { required: true, message: '请选择角色', trigger: 'change' },
  ],
}

const filteredUsers = computed(() => {
  if (!searchQuery.value) return users.value
  return users.value.filter(user =>
    user.username.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const ownerNameMap = computed(() => {
  return Object.fromEntries(users.value.map((user) => [String(user.id), user.username]))
})

const projectRows = computed<AdminProjectRow[]>(() => {
  return projects.value.map((project) => {
    const workflow = workflowMap.value[String(project.id)]
    return {
      ...project,
      workflowState: workflow?.state || project.status || 'draft',
      pendingApprovalStage: workflow?.pending_approval?.stage || null,
      ownerName: ownerNameMap.value[String(project.owner_id)] || `用户 #${project.owner_id}`,
    }
  })
})

const filteredProjects = computed(() => {
  const keyword = projectSearchQuery.value.trim().toLowerCase()
  if (!keyword) return projectRows.value
  return projectRows.value.filter((project) => {
    const title = project.title?.toLowerCase?.() || ''
    const genre = project.genre?.toLowerCase?.() || ''
    const ownerName = project.ownerName?.toLowerCase?.() || ''
    return title.includes(keyword) || genre.includes(keyword) || ownerName.includes(keyword) || String(project.id).includes(keyword)
  })
})

const pendingProjectCount = computed(() => projectRows.value.filter((project) => Boolean(project.pendingApprovalStage)).length)
const taskSquareByProjectId = computed(() => {
  return Object.fromEntries(taskSquareItems.value.map((item) => [String(item.source_project_id), item]))
})

const getRoleLabel = (role: UserRole) => {
  const labels: Record<UserRole, string> = {
    super_admin: '超级管理员',
    admin: '管理员',
    user: '普通用户',
    viewer: '只读用户',
  }
  return labels[role]
}

const getRoleTagType = (role: UserRole) => {
  const types: Record<UserRole, string> = {
    super_admin: 'danger',
    admin: 'warning',
    user: '',
    viewer: 'info',
  }
  return types[role]
}

const loadUsers = async () => {
  if (!userStore.canAccessAdmin) return

  usersLoading.value = true
  try {
    users.value = await getUsers()
  } catch (error) {
    ElMessage.error('加载用户列表失败')
  } finally {
    usersLoading.value = false
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('zh-CN')
}

const projectStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    draft: '草稿',
    pending_confirm: '待方案确认',
    confirmed: '已确认',
    in_progress: '执行中',
    reviewing: '审核中',
    waiting_script_approval: '待剧本审批',
    waiting_storyboard_approval: '待分镜审批',
    waiting_final_review: '待终审',
    waiting_client_acceptance: '待客户验收',
    revision_requested: '待修改',
    revision_in_progress: '修改中',
    completed: '已完成',
    delivered: '已交付',
    on_hold: '已暂停',
    archived: '已归档',
    failed: '失败',
  }
  return labels[status] || status
}

const projectStatusType = (status: string) => {
  if (status.includes('waiting') || status === 'pending_confirm') return 'warning'
  if (status === 'completed') return 'success'
  if (status === 'delivered') return 'success'
  if (status === 'in_progress') return 'primary'
  if (status === 'confirmed' || status === 'revision_in_progress') return 'primary'
  if (status === 'reviewing') return 'warning'
  if (status === 'failed') return 'danger'
  return 'info'
}

const approvalStageLabel = (stage: string | null) => {
  const labels: Record<string, string> = {
    proposal: '方案确认',
    script: '剧本审批',
    storyboard: '分镜审批',
    final: '成品验收',
  }
  return stage ? (labels[stage] || stage) : ''
}

const projectDisplayStatusLabel = (project: AdminProjectRow) => {
  if (project.pendingApprovalStage) {
    return `待${approvalStageLabel(project.pendingApprovalStage)}`
  }
  return projectStatusLabel(project.workflowState)
}

const projectDisplayStatusType = (project: AdminProjectRow) => {
  if (project.pendingApprovalStage) {
    return 'warning'
  }
  return projectStatusType(project.workflowState)
}

const projectPrimaryActionLabel = (project: AdminProjectRow) => {
  if (project.pendingApprovalStage) {
    return '立即处理'
  }
  if (project.workflowState === 'completed' || project.workflowState === 'delivered') {
    return '查看结果'
  }
  if (project.workflowState === 'draft') {
    return '进入工作台'
  }
  if (project.workflowState === 'failed' || project.workflowState === 'on_hold') {
    return '恢复流程'
  }
  return '进入工作台'
}

const projectNextActionLabel = (project: AdminProjectRow) => {
  if (project.pendingApprovalStage) {
    const labels: Record<string, string> = {
      proposal: '等待方案审批',
      script: '等待剧本审批',
      storyboard: '等待分镜审批',
      final: '等待成品验收',
    }
    return labels[project.pendingApprovalStage] || '等待审批'
  }
  if (project.workflowState === 'draft') {
    return '先进入工作台启动流程'
  }
  if (project.workflowState === 'failed') {
    return '排查错误后恢复流程'
  }
  if (project.workflowState === 'on_hold') {
    return '流程已暂停，可恢复继续执行'
  }
  if (project.workflowState === 'completed' || project.workflowState === 'delivered') {
    return '流程已完成，可查看结果'
  }
  return '进入工作台查看当前阶段'
}

const handleProjectPrimaryAction = (project: AdminProjectRow) => {
  if (project.pendingApprovalStage) {
    router.push(buildGenerationRoute(project.id, { stage: project.pendingApprovalStage as any }))
    return
  }

  if (project.workflowState === 'completed' || project.workflowState === 'delivered') {
    router.push(buildGenerationRoute(project.id))
    return
  }

  router.push(buildGenerationRoute(project.id))
}

const loadProjects = async () => {
  projectsLoading.value = true
  try {
    const response = await projectApi.getList({ all_projects: true, page_size: 100 }, { suppressErrorMessage: true })
    projects.value = response.items || []

    const workflows = await Promise.all(
      (response.items || []).map(async (project) => {
        try {
          const workflow = await getWorkflow(Number(project.id))
          return [String(project.id), workflow] as const
        } catch {
          return null
        }
      })
    )

    workflowMap.value = Object.fromEntries(workflows.filter(Boolean) as Array<readonly [string, WorkflowInstance]>)
  } catch (error) {
    ElMessage.error('加载项目列表失败')
  } finally {
    projectsLoading.value = false
  }
}

const loadTaskSquare = async () => {
  taskSquareLoading.value = true
  try {
    const response = await taskSquareApi.list({ suppressErrorMessage: true })
    taskSquareItems.value = response.items || []
  } catch {
    ElMessage.error('加载任务广场失败')
  } finally {
    taskSquareLoading.value = false
  }
}

const handleOpenTaskSquareTab = async () => {
  activeTab.value = 'task-square'
  if (!taskSquareItems.value.length) {
    await loadTaskSquare()
  }
}

const getProjectTaskSquareItem = (projectId: string | number) => {
  return taskSquareByProjectId.value[String(projectId)] || null
}

const projectTaskActionLabel = (project: AdminProjectRow) => {
  return getProjectTaskSquareItem(project.id) ? '查看任务' : '发布任务'
}

const handleProjectTaskAction = async (project: AdminProjectRow) => {
  if (getProjectTaskSquareItem(project.id)) {
    await handleOpenTaskSquareTab()
    return
  }
  await handlePublishProjectTask(project)
}

const handleProjectMenuCommand = async (project: AdminProjectRow, command: string | number | object) => {
  if (command === 'workspace') {
    router.push(buildGenerationRoute(project.id))
    return
  }
  if (command === 'task-square') {
    await handleOpenTaskSquareTab()
    return
  }
  if (command === 'delete') {
    await deleteProject(project)
  }
}

const handlePublishProjectTask = async (project: AdminProjectRow) => {
  publishingProjectId.value = Number(project.id)
  try {
    await taskSquareApi.publish({
      source_project_id: Number(project.id),
      title: project.title,
      summary: String(project.description || project.title || '').slice(0, 180),
      description: project.description || '',
      genre: project.genre || undefined,
      target_duration: project.target_duration ? Math.round(Number(project.target_duration) * 60) : undefined,
      allow_multiple_claims: true,
    })
    ElMessage.success('项目已发布到任务广场')
    await loadTaskSquare()
    activeTab.value = 'task-square'
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.detail || '发布任务失败')
  } finally {
    publishingProjectId.value = null
  }
}

const handleUpdateTaskSquareStatus = async (taskId: number, status: TaskSquareStatus) => {
  try {
    await taskSquareApi.updateStatus(taskId, status)
    ElMessage.success('任务状态已更新')
    await loadTaskSquare()
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.detail || '更新任务状态失败')
  }
}

const taskSquareStatusLabel = (status: TaskSquareStatus) => {
  const labels: Record<TaskSquareStatus, string> = {
    open: '可领取',
    paused: '暂停中',
    closed: '已关闭',
  }
  return labels[status] || status
}

const taskSquareStatusType = (status: TaskSquareStatus) => {
  if (status === 'open') return 'success'
  if (status === 'paused') return 'warning'
  return 'info'
}

const deleteProject = async (project: AdminProjectRow) => {
  try {
    await ElMessageBox.confirm(`确定删除项目“${project.title}”吗？`, '删除项目', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning',
    })

    await projectApi.delete(project.id)
    ElMessage.success('项目已删除')
    await loadProjects()
  } catch {
    // cancelled
  }
}

const showAddUserDialog = () => {
  isEdit.value = false
  editingUserId.value = null
  Object.assign(userForm, {
    username: '',
    email: '',
    password: '',
    resetPassword: '',
    role: 'user',
    is_active: true,
  })
  dialogVisible.value = true
}

const editUser = (user: User) => {
  isEdit.value = true
  editingUserId.value = user.id
  Object.assign(userForm, {
    username: user.username,
    email: user.email,
    password: '',
    resetPassword: '',
    role: user.role,
    is_active: user.is_active,
  })
  dialogVisible.value = true
}

const handleSubmit = async () => {
  if (!userFormRef.value) return

  const valid = await userFormRef.value.validate().catch(() => false)
  if (!valid) return

  submitLoading.value = true

  try {
    if (isEdit.value && editingUserId.value !== null) {
      await updateUser(editingUserId.value, {
        email: userForm.email,
        role: userForm.role,
        is_active: userForm.is_active,
        password: userForm.resetPassword || undefined,
      })
      ElMessage.success('用户已更新')
    } else {
      await createUser({
        username: userForm.username,
        email: userForm.email,
        password: userForm.password,
        role: userForm.role,
      })
      ElMessage.success('用户添加成功')
    }

    dialogVisible.value = false
    await loadUsers()
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.detail || error.message || '操作失败')
  } finally {
    submitLoading.value = false
  }
}

const deleteUser = async (user: User) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除用户 "${user.username}" 吗？`,
      '警告',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    )

    await deleteUserRequest(user.id)
    ElMessage.success('用户已删除')
    await loadUsers()
  } catch {
    // User cancelled
  }
}

const handleLogout = () => {
  userStore.logout()
  router.push('/login')
  ElMessage.success('已退出登录')
}

watch(
  () => route.query.tab,
  (tab) => {
    const resolvedTab = resolveAdminTab(tab)
    if (!resolvedTab) {
      return
    }
    if (resolvedTab === 'users' && !userStore.isSuperAdmin) {
      activeTab.value = 'projects'
      return
    }
    activeTab.value = resolvedTab
  },
  { immediate: true }
)

// Check permission on mount
onMounted(() => {
  if (!userStore.canAccessAdmin) {
    ElMessage.error('无权访问管理后台')
    router.push('/dashboard')
    return
  }

  loadUsers()
  loadProjects()
  loadTaskSquare()

  if (!userStore.isSuperAdmin) {
    activeTab.value = 'projects'
  }
})
</script>

<style scoped>
.admin-container {
  min-height: 100vh;
  padding: 28px;
  background:
    radial-gradient(circle at top left, rgba(251, 191, 36, 0.14), transparent 24%),
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 26%),
    linear-gradient(180deg, #f7fafc 0%, #edf3f8 100%);
}

.admin-header {
  max-width: 1240px;
  margin: 0 auto 20px;
  padding: 24px 28px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(12px);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.admin-header h1 {
  margin: 0;
  font-size: 24px;
  color: #1a1a1a;
}

.admin-header__subtitle {
  margin-top: 8px;
  color: #606266;
}

.admin-overview {
  max-width: 1240px;
  margin: 0 auto 20px;
  display: grid;
  gap: 16px;
}

.admin-overview__stats {
  display: grid;
  gap: 16px;
}

.admin-overview__stats {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.admin-stat-card {
  padding: 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
}

.admin-stat-card {
  display: grid;
  gap: 8px;
}

.admin-stat-card strong {
  font-size: 30px;
  color: #1a1a1a;
}

.admin-stat-card span,
.admin-stat-card small {
  color: #606266;
}

.admin-stat-card--accent {
  background: linear-gradient(180deg, rgba(236, 253, 245, 0.92), rgba(255, 255, 255, 1));
}

.admin-stat-card--soft {
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.92), rgba(255, 255, 255, 1));
}

.toolbar-copy span,
.muted-text {
  color: #606266;
}

.toolbar-copy strong {
  color: #1a1a1a;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.username {
  font-weight: 500;
  color: #606266;
}

.admin-tabs {
  max-width: 1240px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.94);
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.tab-content {
  padding: 24px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-copy {
  display: grid;
  gap: 4px;
}

.toolbar-surface {
  padding: 18px 20px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.92), rgba(255, 255, 255, 0.96));
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.toolbar-cluster {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px 16px;
  align-items: center;
}

.toolbar--projects {
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.project-action-cell {
  align-items: center;
}

.assignment-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.assignment-pill {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.08);
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
}

.settings-card {
  border-radius: 12px;
}

.settings-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.settings-card__header h3 {
  margin: 0 0 6px;
}

.settings-card__header p {
  margin: 0;
  color: #606266;
}

.settings-card__body {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #606266;
}

@media (max-width: 1080px) {
  .admin-overview__stats {
    grid-template-columns: 1fr;
  }

  .toolbar-cluster {
    justify-content: flex-start;
  }
}
</style>
